'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '../button';

// ... (O componente InputGroup permanece o mesmo)
function InputGroup(t0) {
  const $ = _c(6);
  const {
    id,
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== id || $[1] !== label) {
    t1 = <label htmlFor={id} className="block text-sm font-medium text-muted mb-1">{label}</label>;
    $[0] = id;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div>{t1}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
type QuoteFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  squareMeters: string;
  location: string;
  urgency: string;
  howYouFoundUs: string;
};
interface Web3FormsResponse {
  success: boolean;
  message?: string;
}
export default function QuoteForm() {
  const router = useRouter();
  const [formData, setFormData] = useState<QuoteFormState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    squareMeters: '',
    location: '',
    urgency: '',
    howYouFoundUs: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e_0: React.FormEvent<HTMLFormElement>) => {
    e_0.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const accessKey = process.env.NEXT_PUBLIC_WEB3FORMS_API_KEY;
      if (!accessKey) {
        throw new Error('Web3Forms API key is not configured. Please add NEXT_PUBLIC_WEB3FORMS_API_KEY to your environment variables.');
      }
      const formDataObj = new FormData();
      formDataObj.append('access_key', accessKey);

      // Add Portuguese labels for better email readability
      formDataObj.append('Nome', formData.firstName);
      formDataObj.append('Apelido', formData.lastName);
      formDataObj.append('E-mail', formData.email);
      formDataObj.append('Telefone', formData.phone || 'Não fornecido');
      formDataObj.append('Metragem (m²)', formData.squareMeters);
      formDataObj.append('Localização', formData.location);
      formDataObj.append('Urgência', formData.urgency);
      formDataObj.append('Como nos encontrou', formData.howYouFoundUs || 'Não especificado');

      // Add subject and reply-to for better email handling
      formDataObj.append('subject', `Nova Solicitação de Orçamento - ${formData.firstName} ${formData.lastName}`);
      formDataObj.append('from_name', `${formData.firstName} ${formData.lastName}`);
      formDataObj.append('replyto', formData.email);
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataObj
      });
      const data = (await response.json()) as Web3FormsResponse;
      if (data.success) {
        router.push('/thank-you');
      } else {
        throw new Error(data.message ?? 'Failed to submit form');
      }
    } catch (error) {
      console.error('Ocorreu um erro ao enviar o formulário:', error);
      setSubmitError('Não foi possível enviar sua solicitação. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const baseInputStyles = `
    block w-full rounded-lg border border-[color:color-mix(in_srgb,var(--color-ink)_18%,transparent)] bg-white p-3
    text-ink placeholder:text-muted/50
    focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent
  `;
  const radioOptionStyles = `
    flex w-full cursor-pointer items-center rounded-lg border border-[color:color-mix(in_srgb,var(--color-ink)_18%,transparent)] bg-white p-3
    text-sm text-ink transition-colors duration-200 hover:border-ink/50
  `;
  const radioOptionCheckedStyles = `border-accent ring-2 ring-accent`;
  return (
    // O atributo `noValidate` pode ser adicionado para desativar a validação do navegador
    // e permitir que o JavaScript a controle, mas por enquanto vamos deixá-lo fora para depuração.
    <form onSubmit={e_1 => {
      void handleSubmit(e_1);
    }} className="grid grid-cols-1 gap-6 rounded-lg border border-[color:color-mix(in_srgb,var(--color-ink)_18%,transparent)] bg-paper p-6 shadow-soft">
      {/* --- O restante do seu formulário JSX permanece exatamente o mesmo --- */}
      {/* --- Informações de Contato --- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InputGroup id="firstName" label="Nome">
          <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={baseInputStyles} required />
        </InputGroup>
        <InputGroup id="lastName" label="Apelido">
          <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={baseInputStyles} required />
        </InputGroup>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InputGroup id="email" label="Endereço de e‑mail">
          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={baseInputStyles} required />
        </InputGroup>
        <InputGroup id="phone" label="Número de telefone">
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={baseInputStyles} />
        </InputGroup>
      </div>

      {/* --- Detalhes do Projeto --- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InputGroup id="squareMeters" label="Qual a metragem aproximada (m²)?">
          <input type="text" id="squareMeters" name="squareMeters" value={formData.squareMeters} onChange={handleChange} className={baseInputStyles} placeholder="Ex.: 50 m²" required />
        </InputGroup>
        <InputGroup id="location" label="Cidade / bairro onde o serviço será realizado">
          <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} className={baseInputStyles} placeholder="Ex.: Lisboa / Porto" required />
        </InputGroup>
      </div>

      {/* --- Urgência (Radio Group) --- */}
      <fieldset id="urgency">
        <legend className="block text-sm font-medium text-muted mb-1">
          Qual a sua urgência para iniciar o projeto?
        </legend>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {['Urgente (próximos 30 dias)', 'Médio (1–3 meses)', 'Apenas a pesquisar / a planear (sem data definida)'].map(option => <label key={option} className={`${radioOptionStyles} ${formData.urgency === option ? radioOptionCheckedStyles : ''}`}>
              <input type="radio" name="urgency" value={option} checked={formData.urgency === option} onChange={handleChange} className="sr-only" required />
              <span>{option}</span>
            </label>)}
        </div>
      </fieldset>

      {/* --- Como nos encontrou (Radio Group) --- */}
      <fieldset id="howYouFoundUs">
        <legend className="block text-sm font-medium text-muted mb-1">
          Como nos encontrou? (opcional)
        </legend>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {['Google', 'Instagram / Facebook', 'Indicação de arquiteto / designer', 'Indicação de amigo', 'Outro'].map(option_0 => <label key={option_0} className={`${radioOptionStyles} ${formData.howYouFoundUs === option_0 ? radioOptionCheckedStyles : ''}`}>
              <input type="radio" name="howYouFoundUs" value={option_0} checked={formData.howYouFoundUs === option_0} onChange={handleChange} className="sr-only" />
              <span>{option_0}</span>
            </label>)}
        </div>
      </fieldset>

      {submitError && <p className="text-sm text-red-600" role="alert">
          {submitError}
        </p>}

      <Button href={undefined} type="submit" variant="primary" id="btn-submit-quote" className="w-full justify-center" disabled={isSubmitting}>
        {isSubmitting ? 'Enviando…' : 'Enviar pedido'}
      </Button>
    </form>
  );
}